<!-- Copyright 2022, Common Good Learning Tools LLC -->
<template><div>
	<div v-if="satchel_loading_status&&show_satchel&&!small_frame&&!minimized" class="k-satchel-inline-overlay-scrim"></div>
	<div
		v-if="satchel_loading_status"
		v-show="show_satchel"
		ref="satchel_inline_outer"
		class="k-satchel-inline-outer"
		:role="accessible_modal_enabled ? 'dialog' : undefined"
		:aria-modal="accessible_modal_enabled ? 'true' : undefined"
		:aria-label="accessible_modal_enabled ? accessible_modal_label : undefined"
		tabindex="-1"
	>
		<div class="k-satchel-control-btns" v-show="satchel_loading_status=='loaded' && show_left_controls">
			<k-btn v-show="!minimized" color="pink accent-3" fab x-small class="k-satchel-inline-control-btn elevation-0" accessible-label="Close user guide" @click.stop="execute_cmd('hide','control-btn')"><k-icon large color="#fff" style="font-size:20px!important">fas fa-xmark</k-icon></k-btn>
			<k-tooltip top v-if="small_frame"><template v-slot:activator="{on}"><k-btn v-on="on" v-if="!force_size" v-show="!minimized" color="#333" fab x-small class="k-satchel-inline-control-btn elevation-0" accessible-label="Expand user guide" @click.stop="expand_user_guide"><k-icon small color="#fff" style="font-size:16px!important">fas fa-maximize</k-icon></k-btn></template>Expand</k-tooltip>
			<k-tooltip top><template v-slot:activator="{on}"><k-btn v-on="on" v-show="!minimized" color="#333" fab x-small class="k-satchel-inline-control-btn elevation-0" :accessible-label="small_frame ? (window_side === 'left' ? 'Dock user guide right' : 'Dock user guide left') : (window_side === 'left' ? 'Dock user guide left' : 'Dock user guide right')" @click.stop="switch_window_side"><k-icon small color="#fff" style="font-size:16px!important">fas {{small_frame ? (window_side === 'left' ? 'fa-angle-double-right' : 'fa-angle-double-left') : (window_side === 'left' ? 'fa-angle-double-left' : 'fa-angle-double-right')}}</k-icon></k-btn></template>{{small_frame ? (window_side === 'left' ? 'Dock Right' : 'Dock Left') : (window_side === 'left' ? 'Dock Left' : 'Dock Right')}}</k-tooltip>
			<k-tooltip top><template v-slot:activator="{on}"><k-btn v-on="on" v-show="allow_minimize" color="#333" fab x-small class="k-satchel-inline-control-btn elevation-0" :accessible-label="minimized ? 'Show user guide' : 'Minimize user guide'" @click.stop="toggle_minimize"><k-icon small color="#fff" style="font-size:16px!important">fas {{minimized ? 'fa-angle-up' : 'fa-angle-down'}}</k-icon></k-btn></template>{{minimized ? 'Show' : 'Minimize'}}</k-tooltip>
			<k-tooltip top><template v-slot:activator="{on}"><k-btn v-on="on" v-if="allow_open_in_new_window" v-show="!minimized" color="#333" fab x-small class="k-satchel-inline-control-btn elevation-0" accessible-label="Open user guide in new window" @click.stop="execute_cmd('open_in_new_window')"><k-icon small color="#fff" style="font-size:13px!important">fas fa-suitcase</k-icon></k-btn></template>Open in new window</k-tooltip> <!-- {{$store.state.site_config.app_name}} -->
		</div>
		<iframe :name="`satchel_iframe${satchel_inline_uuid}`" ref="satchel_iframe" class="k-satchel-inline-iframe elevation-5" :src="iframe_url" :title="accessible_modal_enabled ? accessible_modal_label : 'Embedded Satchel'" tabindex="0"></iframe>
		<div v-if="minimized" class="k-satchel-inline-minimized-overlay-scrim" @click.stop="minimized=false"></div>
	</div>
</div></template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
	},
	data() { return {
		dialog_open: true,
		satchel_origin: '',
		satchel_loading_status: false,
		show_satchel: false,
		establish_connection_tries: 0,
		connection_established: false,
		chooser_callback_fn: null,
		item_clicked_callback_fn: null,
		
		queued_load_framework: null,
		small_frame_local: false,
		minimized: false,

		// if this is specified when we launch SatchelInline, it will be called whenever a framework is chosen (including when the iframe first loads)
		framework_chosen_callback_fn: null,

		// if this is specified when we launch SatchelInline, it will be called whenever a framework's open nodes change (including when the iframe first loads)
		framework_open_nodes_changed_callback_fn: null,

		// if this is specified when the chooser is shown, it will be called if the user clicks to hide the embedded satchel
		embed_hide_callback_fn: null,

		window_side: 'right',	// 'right' or 'left'
		show_left_controls: true,	// can be set to false on launch
		force_size: '',	// 'small' or 'large'
		allow_minimize: false,
		allow_open_in_new_window: false,
		small_frame_top: null,
		small_frame_width: null,

		// interval time (in ms) for running the maintenance_fn
		maintenance_fn_time: 100,

		// if one or more of the following fns are specified when the chooser is shown, they will be called by the maintenance_fn, and if it returns true, the action will be executed
		// this lets us, e.g., have the chooser automatically be hidden if an editor gets closed
		hide_fn: null,
		clear_select_fn: null,

		// pm_verbose: 'show_untrusted',
		pm_verbose: '',

		// establish a uuid for this instance of the SatchelInline component
		satchel_inline_uuid: U.new_uuid(),
		accessible_modal_enabled: false,
		accessible_modal_label: 'User guide',
		accessible_modal_focus_origin: null,
		last_accessible_tab_direction: 1,
	}},
	computed: {
		...mapState(['site_config']),
		...mapGetters([]),
		small_frame: {
			get() {
				// parent can force a size if they wish; otherwise use value from localstorage
				if (this.force_size) return this.force_size == 'small'
				return this.small_frame_local
			},
			set(val) {
				this.small_frame_local = val
				U.local_storage_set('satchel_inline_small_frame', val)
			},
		},
		iframe_url() { 
			// send date as 'v' for cache-busting purposes
			return this.satchel_origin + `?embedded&v=${Date.now()}`
		},
	},
	watch: {
	},
	created() {
	},
	mounted() {
		document.addEventListener('keydown', this.handle_accessible_modal_document_keydown, true)
		document.addEventListener('focusin', this.handle_accessible_modal_focusin, true)
	},
	beforeDestroy() {
		document.removeEventListener('keydown', this.handle_accessible_modal_document_keydown, true)
		document.removeEventListener('focusin', this.handle_accessible_modal_focusin, true)
	},
	methods: {
		get_accessible_modal_container() {
			return this.$refs.satchel_inline_outer || null
		},
		get_accessible_modal_focus_targets() {
			const container = this.get_accessible_modal_container()
			if (!container) return []
			return Array.from(container.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, iframe, [tabindex]:not([tabindex="-1"])')).filter(element => {
				if (!element) return false
				if (element.disabled) return false
				if (element.getAttribute('aria-hidden') === 'true') return false
				const styles = window.getComputedStyle(element)
				if (styles.visibility === 'hidden' || styles.display === 'none') return false
				return element.getClientRects().length > 0
			})
		},
		focus_accessible_modal_initial_target() {
			if (!this.accessible_modal_enabled) return
			this.$nextTick(() => {
				window.setTimeout(() => {
					const focus_target = this.get_accessible_modal_focus_targets()[0] || this.get_accessible_modal_container()
					focus_target?.focus?.()
				}, 50)
			})
		},
		restore_accessible_modal_focus_origin() {
			if (!this.accessible_modal_enabled) return
			this.accessible_modal_focus_origin?.focus?.()
			this.accessible_modal_focus_origin = null
		},
		handle_accessible_modal_document_keydown(event) {
			if (!this.accessible_modal_enabled || !this.show_satchel) return

			if (event.key === 'Escape' || event.key === 'Esc') {
				event.preventDefault()
				event.stopPropagation()
				this.execute_cmd('hide', 'escape')
				return
			}

			if (event.key !== 'Tab') return

			this.last_accessible_tab_direction = event.shiftKey ? -1 : 1

			const container = this.get_accessible_modal_container()
			if (!container) return
			const focus_targets = this.get_accessible_modal_focus_targets()
			if (!focus_targets.length) {
				event.preventDefault()
				container.focus?.()
				return
			}

			const active_element = document.activeElement
			const first_focus_target = focus_targets[0]
			const last_focus_target = focus_targets[focus_targets.length - 1]
			const focus_is_inside = container.contains(active_element)

			if (event.shiftKey) {
				if (!focus_is_inside || active_element === first_focus_target) {
					event.preventDefault()
					last_focus_target.focus()
				}
				return
			}

			if (!focus_is_inside || active_element === last_focus_target) {
				event.preventDefault()
				first_focus_target.focus()
			}
		},
		handle_accessible_modal_focusin(event) {
			if (!this.accessible_modal_enabled || !this.show_satchel) return
			const container = this.get_accessible_modal_container()
			if (!container || container.contains(event.target)) return
			const focus_targets = this.get_accessible_modal_focus_targets()
			if (!focus_targets.length) {
				container.focus?.()
				return
			}
			const target = this.last_accessible_tab_direction < 0 ? focus_targets[focus_targets.length - 1] : focus_targets[0]
			target?.focus?.()
		},
		set_satchel_origin(origin) {
			// if origin is explicitly specified, use this value for satchel_origin
			if (!empty(origin)) {
				this.satchel_origin = origin

			} else {
				////////////////////////////////////////
				// Satchel version of what to do here
				// set satchel_origin; for satchel-within-satchel, we just use location.origin, unless we're on local
				if (window.location.origin.indexOf('localhost') > -1) this.satchel_origin = 'http://localhost:6051'
				else this.satchel_origin = window.location.origin

				////////////////////////////////////////
				// // Cureum version of what to do here
				// // set satchel_origin from the store
				// this.satchel_origin = this.$store.state.site_config.satchel_origin

				////////////////////////////////////////
				// // Sparkl version of what to do here
				// // if origin not specified, we first look for a satchel_origin_override value in the store; this could be sent in, e.g., from a Cureum instance including Sparkl in an iframe
				// if (!empty(this.$store.satchel_origin_override)) {
				// 	this.satchel_origin = this.$store.satchel_origin_override

				// // if satchel_origin_override not found, set satchel_origin from store.site_config
				// } else {
				// 	this.satchel_origin = this.$store.state.site_config.satchel_origin
				// }

				////////////////////////////////////////
				// for debugging we can override this...
				// if (window.location.origin.indexOf('localhost') > -1) this.satchel_origin = 'http://localhost:6051'
				// this.satchel_origin = 'https://hcs.satchelcommons.com'
				// this.satchel_origin = 'http://localhost:6051'
			}
		},

		// from outside this component, code should call execute(); execute_cmd is for internal use only
		execute(cmd, data) {
			if (this.pm_verbose) console.log(sr('PMX HOST ==> execute cmd $1: $2', cmd, JSON.stringify(data)))
			return new Promise((resolve, reject)=>{
				// if the Satchel iframe hasn't finished loading...
				if (this.satchel_loading_status != 'loaded') {
					// if we haven't even started loading...
					if (!this.satchel_loading_status) {
						///////////////////////////////////////////////
						// This is where we initialize the component -- after the parent says to start doing something
						///////////////////////////////////////////////

						// if we haven't yet set the satchel_origin, do so now
						// note that we have to do this now, as opposed to when the component is first created, because incoming data from the initialize service may affect what this is supposed to be
						if (empty(this.satchel_origin)) this.set_satchel_origin()

						// get initial small_frame_local value from lst
						this.small_frame_local = U.local_storage_get('satchel_inline_small_frame', false)

						// setting satchel_loading_status to a non-falsey value renders the iframe, starting satchel loading
						this.satchel_loading_status = 'loading'

						// initialize postMessage; once loading is done, satchel_loading_status will be set to 'loaded', 
						// then pm_initialize will call execute_cms, sending through resolve and reject
						this.pm_initialize(cmd, data, resolve, reject)
						///////////////////////////////////////////////
					}

				} else {
					// else we're already loaded, so send the command
					this.execute_cmd(cmd, data, resolve, reject)
				}
			})
		}, 

		execute_cmd(cmd, data, resolve, reject) {
			// load_framework or jump_to_item (these are command aliases; they do the same thing)
			if (cmd == 'load_framework' || cmd == 'jump_to_item') {
				// save queued_load_framework so we'll know to resolve once we receive a framework_loaded msg back from satchel
				this.queued_load_framework = {
					data: data,
					resolve: resolve,
				}

				// data can specify `framework_identifier` and/or `item_identifier`
				this.pm_send('load_framework', data, null, reject)

			} else if (cmd == 'clear_selected_items') {
				// clear any selected_items that were previously sent in to be shown
				this.pm_send('clear_selected_items')

			} else if (cmd == 'chooser') {
				// data should include 'chooser_mode' (boolean) and could include 'outcomes_only'
				let data_to_send = {
					chooser_mode: data.chooser_mode,
					outcomes_only: data.outcomes_only
				}
				// (data could include other things too (see below), so we can't send the data object in directly)
				this.pm_send('chooser', data_to_send, null, reject)

				// if chooser_mode is false, we're closing the chooser, so satchel won't call us back; therefore resolve right away
				if (data.chooser_mode == false) {
					this.chooser_callback_fn = null
					this.item_clicked_callback_fn = null
					resolve()

				} else {
					// stash resolve in chooser_callback_fn, so the message listener can call it once the user chooses something
					this.chooser_callback_fn = resolve

					// data can also specify an item_clicked_callback_fn
					this.item_clicked_callback_fn = data.item_clicked_callback_fn
				}
			
			} else if (cmd == 'search') {
				this.pm_send('search', {search_terms: data.search_terms, limit_to: data.limit_to}, resolve, reject)
				if (resolve) resolve('ok')

			} else if (cmd == 'open_in_new_window') {
				this.pm_send('open_in_new_window')
				if (resolve) resolve('ok')

			} else if (cmd == 'show') {
				this.accessible_modal_enabled = data?.accessible_modal === true
				this.accessible_modal_label = data?.accessible_modal_label || 'User guide'
				this.accessible_modal_focus_origin = this.accessible_modal_enabled && document.activeElement instanceof HTMLElement ? document.activeElement : null
				this.show_satchel = true
				
				// set or clear the hide_fn, embed_hide_callback_fn, framework_chosen_callback_fn, and framework_open_nodes_changed_callback_fn here -- 
				if (data?.hide_fn) this.hide_fn = data.hide_fn
				else this.hide_fn = null

				if (data?.embed_hide_callback_fn) this.embed_hide_callback_fn = data.embed_hide_callback_fn
				else this.embed_hide_callback_fn = null

				if (data?.framework_chosen_callback_fn) this.framework_chosen_callback_fn = data.framework_chosen_callback_fn
				else this.framework_chosen_callback_fn = null

				if (data?.framework_open_nodes_changed_callback_fn) this.framework_open_nodes_changed_callback_fn = data.framework_open_nodes_changed_callback_fn
				else this.framework_open_nodes_changed_callback_fn = null

				// set default iframe display values
				this.window_side = data?.window_side || 'right'
				this.show_left_controls = (data?.show_left_controls === false) ? false : true	// show_left_controls is true unless explicitly set to false
				this.force_size = data?.force_size || ''
				this.allow_minimize = data?.allow_minimize || false
				this.allow_open_in_new_window = data?.allow_open_in_new_window || false
				this.small_frame_top = data?.small_frame_top || -1

				// small_frame_width is used as the mininimum width; if caller wants to set a max width, send small_frame_width_max
				this.small_frame_width = data?.small_frame_width || 448
				this.small_frame_width_max = data?.small_frame_width_max || 600

				// if start_size is provided, start with that size, but let the user switch
				if (!empty(data?.start_size)) {
					this.small_frame_local = (data.start_size == 'small')
					U.local_storage_set('satchel_inline_small_frame', this.small_frame_local)
				}

				// start the maintenance_fn, which does things like setting the iframe location if the window size changes
				this.maintenance_fn()
				
				// if z-index is specified, use it
				let z_index
				if (!empty(data.z_index)) z_index = data.z_index
				// otherwise make sure this satchel window is showing above any other satchel windows that might also be rendered
				else {
					if (empty(this.$store.state.satchel_inline_z_index)) this.$store.state.satchel_inline_z_index = 10000
					else ++this.$store.state.satchel_inline_z_index
					z_index = this.$store.state.satchel_inline_z_index
				}

				$(this.$el).find('.k-satchel-inline-outer').css('z-index', z_index)

				this.focus_accessible_modal_initial_target()

				if (resolve) resolve('ok')

			} else if (cmd == 'hide') {
				// always reset chooser mode before hiding; no need to do anything after it finishes
				this.pm_send('chooser', {chooser_mode: false})

				// if we have an embed_hide_callback_fn, call it, sending in the data parameter (which would be 'control-btn' if execute_cmd was called via the user clicking the control btn)
				if (this.embed_hide_callback_fn) this.embed_hide_callback_fn(data)

				// clear all maintenance fns
				this.hide_fn = null
				this.clear_select_fn = null
				this.embed_hide_callback_fn = null

				this.show_satchel = false
				this.restore_accessible_modal_focus_origin()
				if (resolve) resolve('ok')
			}
		},

		// this is the initialize fn for window A that embeds/opens window B
		// here, we establish the eventListener for postMessages, then send a message to window B establishing the connection.
		pm_initialize(cmd, cmd_data, resolve, reject) {
			// set up eventListener for postMessages
			window.addEventListener('message', (event) => {
				// console.warn('received event for ' + this.satchel_inline_uuid)
				// event.source is the window that sent the message (the window that included the iframe)
				// event.origin is the origin that sent it; make sure we trust it
				// event.data is what was sent

				// Do we trust the sender of this message?
				if (event.origin !== this.satchel_origin) {
					if (this.pm_verbose == 'show_untrusted') {
						if (event.origin == window.location.origin) console.log('   PMX HOST    !-- message in sparkl from self')
						else console.log('   PMX HOST    !-- message in sparkl from untrusted origin: ' + event.origin, event.data)
					}
					return
				}

				// messages should all have the form {msg: 'xxx', data: ...}
				if (typeof(event.data) != 'object' || empty(event.data.msg)) {
					if (event.data.type?.indexOf('webpack') != 0 && event.data?.indexOf('webpack') != 0) {
						console.log('   PMX HOST    !-- bad message received', event.data)
					}
					return
				}
				let msg = event.data.msg
				let data = event.data.data

				if (msg == 'received_establish_connection') {
					if (this.pm_verbose) console.log('   PMX HOST    <-- CONNECTION ESTABLISHED WITH SATCHEL')
					this.satchel_loading_status = 'loaded'
					this.connection_established = true
					U.loading_stop()

					// if the original execute command wasn't 'show', assume that the user wants the iframe to appear first
					// (if the original command *was* show, go ahead and execute it; then we'll go on to execute whatever was in resolve)
					if (cmd != 'show') this.show_satchel = true

					// then execute the command that sent to the original execute call (wait a tick in case we just showed)
					this.$nextTick(()=>this.execute_cmd(cmd, cmd_data, resolve, reject))
				
				///////////////////////////////////////////
				// HERE ARE THE MESSAGES THAT SATCHEL MAY SEND BACK TO US
				} else if (msg == 'framework_loaded') {
					// console.warn('framework_loaded: ' + data.framework_identifier)
					if (this.pm_verbose) console.log('   PMX HOST    <-- framework_loaded: ' + JSON.stringify(data))
					if (this.queued_load_framework) {
						if (!this.queued_load_framework.data.framework_identifier || data.framework_identifier == this.queued_load_framework.data.framework_identifier) {
							// set or clear the clear_select_fn here, when the framework has been loaded
							if (this.queued_load_framework.data?.clear_select_fn) this.clear_select_fn = data.clear_select_fn
							else this.clear_select_fn = null

							if (this.pm_verbose) console.log('   PMX HOST    <-- resolving framework load')
							this.queued_load_framework.resolve('ok')
							this.queued_load_framework = null
						}
					}
					if (this.framework_chosen_callback_fn) {
						this.framework_chosen_callback_fn(data.framework_identifier)
					}

				// item_chosen: user clicked the checkbox for an item
				} else if (msg == 'item_chosen') {
					if (this.pm_verbose) console.log('   PMX HOST    <-- item_chosen: ' + JSON.stringify(data))
					vapp.show_satchel = false
					let cfitem = data
					// a callback_fn should have been specified, but check just in case
					if (this.chooser_callback_fn) {
						this.chooser_callback_fn(cfitem)
						this.chooser_callback_fn = null
						this.item_clicked_callback_fn = null	// clear this fn too when the chooser is closed
						// note that the callback fn should call execute('hide')
					}

				// item_clicked: user clicked the text (not the checkbox or the icon) for an item
				} else if (msg == 'item_clicked') {
					if (this.pm_verbose) console.log('   PMX HOST    <-- item_clicked: ' + JSON.stringify(data))
					// data should include a framework_identifier and a cfitem, as well as open_nodes, which is an object indexed by node indexes
					// a item_clicked_callback_fn may or may not have have been specified; if not specified, the app doesn't care about clicks
					if (this.item_clicked_callback_fn) {
						this.item_clicked_callback_fn(data)
					}
					// note that on a click, we *don't* expect the embedded Satchel to be closed

				// framework_open_nodes_changed: open_nodes changed for the framework
				} else if (msg == 'framework_open_nodes_changed') {
					if (this.pm_verbose) console.log('   PMX HOST    <-- framework_open_nodes_changed: ' + JSON.stringify(data))
					if (this.framework_open_nodes_changed_callback_fn) {
						this.framework_open_nodes_changed_callback_fn(data)
					}

				// more messages here...
				} else {
					if (this.pm_verbose) console.log('   PMX HOST    !-- received unprocessed message: ', msg, data)
				}
			}, false)

			// show loader, then start trying to establish the connection
			U.loading_start()
			setTimeout(x=>{ this.pm_establish_connection() }, 100)
		},

		pm_establish_connection() {
			// window B may not be ready to receive messages right away, so keep sending this message until it succeeds
			if (!this.connection_established) {
				if (this.establish_connection_tries > 20) {
					console.log('   PMX HOST    !-- giving up on establishing connection')
					U.loading_stop()
					vapp.$alert('The connection to Satchel could not be opened.')
					return
				}
				++this.establish_connection_tries
				if (this.pm_verbose) console.log('   PMX HOST    --> queuing establish_connection message (' + this.establish_connection_tries + ')')
				this.pm_send('establish_connection')

				setTimeout(x=>{ this.pm_establish_connection() }, 500)
			}
		},

		pm_send(msg, data, resolve, reject) {
			if (this.pm_verbose) console.log('   PMX HOST    --> pm_send: ' + msg)

			// queue a message to be sent; second param specifies what the origin of the target window must be for the event to be dispatched
			try {
				if (msg == 'establish_connection' && this.establish_connection_tries == 1) {
					// in console, note that we may get an error message here
					console.log('   PMX HOST    --> NOTE: “Failed to execute \'postMessage\' on \'DOMWindow\'” message may occur on first `establish_connection` message try')
				}
				// in case there are multiple SatchelInline components, specify the iframe name like so
				window[`satchel_iframe${this.satchel_inline_uuid}`].postMessage({msg: msg, data: data}, this.satchel_origin)
				if (resolve) resolve('ok')

			} catch(e) {
				console.log('   PMX HOST    !-- pm_send error caught: ', e)
				if (reject) reject(e)
			}
		},

		expand_user_guide(evt) {
			this.small_frame = false
			this.maintenance_fn()
			if (!empty(evt) && !empty(evt.target)) $(evt.target).closest('button').blur()
		},

		toggle_minimize(evt) {
			this.minimized = !this.minimized
			this.maintenance_fn()
			if (!empty(evt) && !empty(evt.target)) $(evt.target).closest('button').blur()
		},

		// this fn constantly runs while the satchel component is loaded, and does "maintenance" things like setting the iframe wrapper boundaries
		maintenance_fn() {
			// if hide_fn is set, check it, and if it succeeds, hide the satchel component
			if (this.hide_fn && this.hide_fn()) {
				this.execute_cmd('hide')
				return
			}

			// if clear_select_fn is set, check it, and if it succeeds, clear the selected items
			if (this.clear_select_fn && this.clear_select_fn()) {
				this.execute_cmd('clear_selected_items')
				return
			}

			// reset v-application width if we're not showing this satchel component
			if (!this.satchel_loading_status || !this.show_satchel) {
				$('.v-application').css('width', 'auto')
				$('.v-dialog__content--active').css('width', '100%')
			}

			// if satchel has finished loading, and we're no longer showing the iframe, return now
			if (this.satchel_loading_status == 'loaded' && !this.show_satchel) return
			// else schedule this fn to run again in maintenance_fn_time ms
			else setTimeout(()=>this.maintenance_fn(), this.maintenance_fn_time)

			// set width and height of iframe, depending on settings for minimized and small_frame
			let $jq = $(this.$el)
			let ww = $(window).width()
			let wh = $(window).height()

			// minimized means it's been shoved down into the lower-right corner
			if (this.minimized) {
				if (this.window_side === 'left') {
					let sw = Math.round(ww / 3)
					if (sw > 500) sw = 500

					$jq.find('.k-satchel-inline-outer').css({
						'width': sw + 'px',
						'left': '0px',
						'right': 'auto',
						'height': '46px',
						'top': (wh - 46)+'px',
					})
					$jq.find('.k-satchel-inline-minimized-overlay-scrim').css({
						'width': sw + 'px',
					})
					$jq.find('.k-satchel-inline-iframe').css({
						'border-radius': '0',
					})
					$jq.find('.k-satchel-control-btns').css({
						'left': 'auto',
						'right': '-42px',
						'border-radius': '0 12px 12px 0',
					})

					$('.v-application').css('width', 'auto')
					$('.v-dialog__content--active').css('width', '100%')
					return

				} else {
					let sw = Math.round(ww / 3)
					if (sw > 500) sw = 500

					$jq.find('.k-satchel-inline-outer').css({
						'width': sw + 'px',
						'left': 'auto',
						'right': '0px',
						'height': '46px',
						'top': (wh - 46)+'px',
					})
					$jq.find('.k-satchel-inline-minimized-overlay-scrim').css({
						'width': sw + 'px',
					})
					$jq.find('.k-satchel-inline-iframe').css({
						'border-radius': '0',
					})
					$jq.find('.k-satchel-control-btns').css({
						'left': '-42px',
						'right': 'auto',
						'border-radius': '12px 0 0 12px',
					})

					$('.v-application').css('width', 'auto')
					$('.v-dialog__content--active').css('width', '100%')
					return

				}
			}

			// if window width is < 600, we can't show the large iframe
			if (this.small_frame || ww < 600) {
				let sw = Math.round(ww / 3)
				if (sw > this.small_frame_width_max) sw = this.small_frame_width_max
				if (sw < this.small_frame_width) sw = this.small_frame_width

				// if window width is < 600, use the full width of the window (this happens, e.g., in ResourceQuickAlign if the user forces the "outer SatchelInline" to small width
				if (ww < 600) sw = ww

				let sh = wh + 2
				if (this.small_frame_top > -1) {
					$jq.find('.k-satchel-inline-iframe').css({
						'border-radius': '0',
					})
					$jq.find('.k-satchel-control-btns').css({
						'border-radius': (this.window_side == 'right') ? '12px 0 0 12px' : '0 12px 12px',
					})
					sh -= this.small_frame_top

				} else {
					$jq.find('.k-satchel-inline-iframe').css({
						'border-radius': '0',
					})
					$jq.find('.k-satchel-control-btns').css({
						'border-radius': (this.window_side == 'right') ? '0 0 0 12px' : '0 0 12px 0',
					})
				}

				if (this.window_side == 'right') {
					$jq.find('.k-satchel-inline-outer').css({
						'left': 'auto',
						'right': '0px',
					})
					$jq.find('.k-satchel-control-btns').css({
						'left': '-42px',
						'right': 'auto',
					})

				} else {	// left
					$jq.find('.k-satchel-inline-outer').css({
						'left': '0px',
						'right': 'auto',
					})
					$jq.find('.k-satchel-control-btns').css({
						'left': 'auto',
						'right': '-42px',
					})
				}

				$jq.find('.k-satchel-inline-outer').css({
					'width': sw + 'px',
					'height': `${sh}px`,
					'top': `${this.small_frame_top}px`,
				})

				// set v-application, and all dialogs, to the left-over width
				// let app_width = (ww - sw)+'px'
				// $('.v-application').css('width', app_width)
				// $('.v-dialog__content--active').css('width', app_width)

			} else {
				let sw = ww - 100
				let sh = wh - 40
				if (sw > 1200) sw = 1200

				$jq.find('.k-satchel-inline-outer').css({
					'width': sw + 'px',
					'left': sr('calc(50vw - $1)', Math.round(sw/2) + 'px'),
					'right': 'auto',
					'height': sh + 'px',
					'top': '20px',
				})
				$jq.find('.k-satchel-inline-iframe').css({
					'border-radius': '0 12px 12px 12px',
				})
				$jq.find('.k-satchel-control-btns').css({
					'left': '-42px',
					'right': 'auto',
					'border-radius': '12px 0 0 12px',
				})

				$('.v-application').css('width', 'auto')
				$('.v-dialog__content--active').css('width', '100%')
			}
		},

		switch_window_side(evt) {
			if (this.small_frame === true) {
				this.window_side = this.window_side === 'right' ? 'left' : 'right'
				this.maintenance_fn()
			} else {
				this.small_frame = true
				this.maintenance_fn()
			}
			if (!empty(evt) && !empty(evt.target)) $(evt.target).closest('button').blur()
		}
	}
}
</script>

<style lang="scss">
.k-satchel-inline-outer {
	position:fixed;
	z-index:10000;
	border-radius:12px;
	transition: top .2s, height .2s;
	// top/left/width/top/height will be set by maintenance_fn
}

.k-satchel-inline-iframe {
	width:100%;
	height:100%;
	border-radius:12px;
	border:1px solid #999;
	background-color:#999;
}

.k-satchel-control-btns {
	position:absolute;
	left:-42px;
	width:42px;
	background-color:rgba(153,153,153,0.8);
	border-radius:0 0 0 12px;
	padding:6px 6px 6px 8px;
	min-height:64px;
}

.k-satchel-inline-control-btn {
	// position:absolute;
	// right:-14px;
	// top:-10px;
	width:27px!important;
	height:27px!important;
	margin:3px 0;
	border:1px solid #999!important;
}

.k-satchel-inline-overlay-scrim {
	position:fixed;
	z-index:9999;
	left:0;
	top:0;
	width:100vw;
	height:100vh;
	opacity:0.8; 
	background-color:#000;
}

.k-satchel-inline-minimized-overlay-scrim {
	position:absolute;
	right:0;
	bottom:0;
	height:46px;
	background-color:transparent;
}
</style>
